// src/slices/ticketReturnAppraisal/thunk.ts
// Async thunks cho US-018 — Thẩm định & phê duyệt hồ sơ thu hồi vé ế.
// Bám đúng TicketReturnReconciliationController (query PascalCase, appraise trả về `long`).

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getTicketReturnReconciliationsApi,
  getTicketReturnReconciliationByIdApi,
  appraiseTicketReturnReconciliationApi,
} from 'helpers/fakebackend_helper';
import { AppraisalDecision } from 'slices/ticketReturnReconciliation/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_return_appraisal_messages');

interface FetchReconciliationsParams {
  // "ChoThamDinh" | "DaThamDinh" | "1" | "2"
  status?: string;
  agentId?: number | string;
  pageIndex?: number;
  pageSize?: number;
}

const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Danh sách phiếu đối soát. LƯU Ý: endpoint chỉ hỗ trợ lọc Status + AgentId (không có
// IssuancePlanId) — BE cũng chỉ trả phiếu đã IsFrozen (đã chuyển thẩm định thật sự).
export const fetchAppraisalReconciliations = createAsyncThunk(
  'TicketReturnAppraisal/fetchList',
  async (params: FetchReconciliationsParams, { rejectWithValue }) => {
    try {
      const response = await getTicketReturnReconciliationsApi(
        cleanParams({
          Status: params.status,
          AgentId: params.agentId,
          PageIndex: params.pageIndex,
          PageSize: params.pageSize,
        }),
      );
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_danh_sach_phieu_message'));
    }
  },
);

// Chi tiết phiếu đối soát — response đã kèm `attachmentUrl` (signed URL, TTL 1h) nên KHÔNG có
// endpoint file-url riêng.
export const fetchAppraisalReconciliationById = createAsyncThunk(
  'TicketReturnAppraisal/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getTicketReturnReconciliationByIdApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_chi_tiet_phieu_message'));
    }
  },
);

/**
 * Thẩm định: PheDuyet / TuChoi / YeuCauDoiSoatLai.
 * Endpoint chỉ trả về `long` (id) chứ không trả DTO đầy đủ — nơi gọi phải refetch danh sách để
 * thấy trạng thái mới.
 */
export const appraiseReconciliation = createAsyncThunk(
  'TicketReturnAppraisal/appraise',
  async (
    { id, decision, comment }: { id: number; decision: AppraisalDecision; comment?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await appraiseTicketReturnReconciliationApi(id, { decision, comment });
      return (response as any)?.data ?? response ?? id;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_luu_ket_qua_tham_message'));
    }
  },
);
